import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  FileText,
  ClipboardList,
  Target,
  Send,
  Check,
  Sparkles,
  Play,
  Mail,
  ChevronRight,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Placeholder welcome video — swap `videoUrl` with a real YouTube link when ready
const welcomeVideo = {
  title: 'A Welcome From Jill',
  description: 'A short hello & what to expect as a new client.',
  videoUrl: '', // e.g. 'https://youtu.be/XXXXXXXXXXX'
};

// Placeholder resources — add real PDF urls (or internal routes) when ready
const waivers = [
  { title: 'Liability Waiver', description: 'Required before your first session.', url: '' },
  { title: 'Health & Medical Disclosure', description: 'Helps Jill tailor sessions safely.', url: '' },
];

const questionnaires = [
  { title: 'Zoom Meet-Up · Preferred Time Votes', description: 'Help pick the best times for 28 Day Reset group Zoom calls.', url: '/poll/zoom-times' },
  { title: 'The Vitality Potential Tool', description: 'A snapshot of sleep, stress, nutrition, and movement.', url: '/quiz/lifestyle-assessment' },
  { title: 'Wellness Journal', description: 'Your dated session log — available to V.I.P. members & package holders.', url: '/journal', vip: true },
  { title: 'Metabolic Typing Questionnaire', description: 'Discover the way of eating that suits your body.', url: '/quiz/metabolic-rhythm' },
  { title: 'Food Preferences', description: 'Private clients: tick the foods you love or need to avoid so Jill can shape your recipes.', url: '/quiz/food-preferences', vip: true },
  { title: 'Wellness Wheel', description: 'Where are you now across 8 dimensions of wellness?', url: '/programs/terrain/wellness-wheel' },
];

const getYouTubeId = (url) => {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^&\n?#]+)/);
  return m ? m[1] : null;
};

const NewClientPage = () => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    goals: '',
    experience: '',
    heard_from: '',
    consent_waiver: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim() || !form.email.trim()) {
      setError('Please share your name and email.');
      return;
    }
    if (!form.consent_waiver) {
      setError('Please agree to the waiver acknowledgement to continue.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/new-client/intake`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || 'Something went wrong. Please try again.');
      }
      setSubmitted(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const welcomeYtId = getYouTubeId(welcomeVideo.videoUrl);

  return (
    <div className="min-h-screen bg-cream py-12 px-4" data-testid="new-client-page">
      <div className="max-w-5xl mx-auto">
        {/* Back link */}
        <Link
          to="/programs"
          className="inline-flex items-center gap-2 text-forest/70 hover:text-forest font-body text-sm mb-8"
          data-testid="new-client-back-link"
        >
          <ArrowLeft size={16} /> Back to Programs
        </Link>

        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-10"
        >
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={16} className="text-[#8a6a5a]" />
            <p className="text-xs font-body text-[#8a6a5a] uppercase tracking-[0.25em]">
              New Client Intake
            </p>
          </div>
          <h1 className="font-heading text-4xl md:text-5xl text-forest mb-3">
            Welcome — let's get you started.
          </h1>
          <p className="text-stone-600 font-body max-w-2xl leading-relaxed">
            A quiet little landing pad before we begin. Watch the welcome note, complete a
            few foundational questionnaires at your own pace, sign the waiver, and share a
            little about yourself and your goals. I'll take it from there.
          </p>
        </motion.div>

        {/* Welcome video */}
        <motion.section
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-10 bg-[#f5efe4] border border-stone-200 rounded-2xl overflow-hidden"
          data-testid="new-client-welcome-video"
        >
          <div className="grid grid-cols-1 md:grid-cols-5">
            <div className="md:col-span-3 aspect-video bg-forest/10">
              {welcomeYtId ? (
                <iframe
                  src={`https://www.youtube.com/embed/${welcomeYtId}`}
                  title={welcomeVideo.title}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center text-stone-400">
                  <Play size={32} className="mb-2 opacity-60" />
                  <p className="text-xs uppercase tracking-[0.2em] font-body">
                    Welcome video coming soon
                  </p>
                </div>
              )}
            </div>
            <div className="md:col-span-2 p-6 md:p-8 flex flex-col justify-center">
              <p className="text-xs font-body text-[#8a6a5a] uppercase tracking-[0.25em] mb-2">
                Start Here
              </p>
              <h2 className="font-heading text-2xl text-forest mb-2">{welcomeVideo.title}</h2>
              <p className="text-stone-600 font-body text-sm leading-relaxed">
                {welcomeVideo.description}
              </p>
            </div>
          </div>
        </motion.section>

        {/* Two-column: Waivers + Questionnaires */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-10">
          {/* Waivers */}
          <section
            className="bg-white border border-stone-200 rounded-2xl p-6"
            data-testid="new-client-waivers"
          >
            <div className="flex items-center gap-2 mb-4">
              <FileText size={18} className="text-[#8a6a5a]" />
              <h3 className="font-heading text-xl text-forest">Waivers</h3>
            </div>
            <ul className="space-y-3">
              {waivers.map((w, i) => (
                <li
                  key={i}
                  className="flex items-start justify-between gap-4 p-3 rounded-xl bg-[#faf6ef] border border-stone-100"
                >
                  <div>
                    <p className="font-body text-forest font-medium text-sm">{w.title}</p>
                    <p className="text-xs text-stone-500 font-body">{w.description}</p>
                  </div>
                  {w.url ? (
                    <a
                      href={w.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs font-body text-forest underline whitespace-nowrap self-center"
                    >
                      View
                    </a>
                  ) : (
                    <span className="text-xs font-body text-stone-400 whitespace-nowrap self-center">
                      Coming soon
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </section>

          {/* Questionnaires */}
          <section
            className="bg-white border border-stone-200 rounded-2xl p-6"
            data-testid="new-client-questionnaires"
          >
            <div className="flex items-center gap-2 mb-4">
              <ClipboardList size={18} className="text-[#4a7a7a]" />
              <h3 className="font-heading text-xl text-forest">Questionnaires</h3>
            </div>
            <ul className="space-y-3">
              {questionnaires.map((q, i) => (
                <li
                  key={i}
                  className="flex items-start justify-between gap-4 p-3 rounded-xl bg-[#eef4f4] border border-stone-100"
                >
                  <div>
                    <p className="font-body text-forest font-medium text-sm flex items-center gap-2">
                      {q.title}
                      {q.vip && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-body uppercase tracking-wider bg-[#8a6a5a]/10 text-[#8a6a5a] rounded-full px-2 py-0.5" data-testid="questionnaire-vip-badge">
                          V.I.P.
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-stone-500 font-body">{q.description}</p>
                  </div>
                  <Link
                    to={q.url}
                    className="inline-flex items-center gap-1 text-xs font-body text-forest underline whitespace-nowrap self-center"
                  >
                    {q.vip ? 'View' : 'Start'} <ChevronRight size={12} />
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        </div>

        {/* Intake form */}
        <motion.section
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-white border border-stone-200 rounded-2xl p-6 md:p-10"
          data-testid="new-client-intake-form"
        >
          <div className="flex items-center gap-2 mb-2">
            <Target size={18} className="text-forest" />
            <p className="text-xs font-body text-[#8a6a5a] uppercase tracking-[0.25em]">
              Your Details & Goals
            </p>
          </div>
          <h2 className="font-heading text-2xl md:text-3xl text-forest mb-2">
            Tell me a little about you
          </h2>
          <p className="text-stone-600 font-body text-sm mb-6">
            Anything you'd like me to know before we meet. I'll reach out personally.
          </p>

          {submitted ? (
            <div
              className="bg-forest/5 border border-forest/20 rounded-xl p-6 text-center"
              data-testid="new-client-success"
            >
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-forest text-cream mb-3">
                <Check size={22} />
              </div>
              <h3 className="font-heading text-xl text-forest mb-2">
                Thank you, {form.name.split(' ')[0]}.
              </h3>
              <p className="text-stone-600 font-body text-sm max-w-md mx-auto">
                Your intake is with me. I'll be in touch shortly at{' '}
                <span className="font-medium text-forest">{form.email}</span>.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-body text-stone-600 mb-1">
                    Full Name <span className="text-terracotta">*</span>
                  </label>
                  <input
                    name="name"
                    type="text"
                    value={form.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-stone-300 font-body text-sm focus:outline-none focus:border-forest"
                    data-testid="new-client-name"
                  />
                </div>
                <div>
                  <label className="block text-xs font-body text-stone-600 mb-1">
                    Email <span className="text-terracotta">*</span>
                  </label>
                  <input
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-stone-300 font-body text-sm focus:outline-none focus:border-forest"
                    data-testid="new-client-email"
                  />
                </div>
                <div>
                  <label className="block text-xs font-body text-stone-600 mb-1">Phone</label>
                  <input
                    name="phone"
                    type="tel"
                    value={form.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 rounded-lg border border-stone-300 font-body text-sm focus:outline-none focus:border-forest"
                    data-testid="new-client-phone"
                  />
                </div>
                <div>
                  <label className="block text-xs font-body text-stone-600 mb-1">
                    How did you hear about Jill?
                  </label>
                  <input
                    name="heard_from"
                    type="text"
                    value={form.heard_from}
                    onChange={handleChange}
                    placeholder="Friend, Instagram, Google…"
                    className="w-full px-4 py-2.5 rounded-lg border border-stone-300 font-body text-sm focus:outline-none focus:border-forest"
                    data-testid="new-client-heard-from"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-body text-stone-600 mb-1">
                  What are your main goals?
                </label>
                <textarea
                  name="goals"
                  rows={4}
                  value={form.goals}
                  onChange={handleChange}
                  placeholder="Release chronic back pain, build energy, feel at home in my body…"
                  className="w-full px-4 py-2.5 rounded-lg border border-stone-300 font-body text-sm focus:outline-none focus:border-forest resize-none"
                  data-testid="new-client-goals"
                />
              </div>

              <div>
                <label className="block text-xs font-body text-stone-600 mb-1">
                  Previous movement / wellness experience
                </label>
                <textarea
                  name="experience"
                  rows={3}
                  value={form.experience}
                  onChange={handleChange}
                  placeholder="Yoga, Pilates, physio, injuries, surgeries, anything worth knowing…"
                  className="w-full px-4 py-2.5 rounded-lg border border-stone-300 font-body text-sm focus:outline-none focus:border-forest resize-none"
                  data-testid="new-client-experience"
                />
              </div>

              <label className="flex items-start gap-3 p-3 rounded-lg bg-[#faf6ef] border border-stone-200">
                <input
                  name="consent_waiver"
                  type="checkbox"
                  checked={form.consent_waiver}
                  onChange={handleChange}
                  className="mt-1 accent-forest"
                  data-testid="new-client-consent"
                />
                <span className="text-xs font-body text-stone-600 leading-relaxed">
                  I acknowledge I've reviewed (or will review) the waivers above and agree to
                  sign the required forms before my first session.
                </span>
              </label>

              {error && (
                <p className="text-sm text-terracotta font-body" data-testid="new-client-error">
                  {error}
                </p>
              )}

              <div className="flex flex-wrap items-center gap-4 pt-2">
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center gap-2 bg-forest text-cream font-body font-medium px-6 py-3 rounded-full hover:bg-forest-dark transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="new-client-submit"
                >
                  {submitting ? 'Sending…' : (
                    <>
                      Send My Intake <Send size={14} />
                    </>
                  )}
                </button>
                <a
                  href="mailto:heyjbennett@yahoo.ca?subject=New Client Inquiry"
                  className="inline-flex items-center gap-1 text-sm font-body text-forest hover:text-forest-dark"
                >
                  <Mail size={14} /> or email Jill directly
                </a>
              </div>
            </form>
          )}
        </motion.section>
      </div>
    </div>
  );
};

export default NewClientPage;
